import { useEffect, useRef, useState } from "react";
import i18next from "i18next";
import Cookies from "js-cookie";
import './AgeVerification.scss';
import Loading from "modules/Loading/Loading";

interface AgeVerificationProps {
    minAge?: number | null;
    children: React.ReactNode;
}

const AgeVerificationCookieKey = 'age_verified';

const AgeVerification = ({ minAge = null, children }: AgeVerificationProps) => {

    const dateValues = useRef({ day: null, month: null, year: null });

    const [ loading, setLoading ] = useState<boolean>( true );
    const [ buttonDisabled, setButtonDisabled ] = useState<boolean>( true );
    const [ isVerified, setIsVerified ] = useState<boolean|null>( null );

    useEffect(() => {
        // Check if the user is already verified
        const cookieValue = Cookies.get( AgeVerificationCookieKey );
        setIsVerified( cookieValue === 'true' ? true : null );

        // Simulate loading state
        setTimeout(() => {
            setLoading(false);
        }, 100);
    }, [] )

    const isAllFilledIn = () => {
        // Check if all date values are filled in
        const { day, month, year } = dateValues.current;

        if( !day || day.toString().length < 1 || !month || month.toString().length < 1 || !year || year.toString().length !== 4 ) {
            return false;
        }

        return day !== null && month !== null && year !== null;
    }

    const isValidAge = () => {
        // Validate the date input
        const { day, month, year } = dateValues.current;
        
        if( !day || !month || !year ) {
            return false;
        }

        // Create a date object from the input values
        const inputDate = new Date(year, month - 1, day);
        const today = new Date();

        // Calculate the age
        let age = today.getFullYear() - inputDate.getFullYear();
        const monthDiff = today.getMonth() - inputDate.getMonth();
        if( monthDiff < 0 || (monthDiff === 0 && today.getDate() < inputDate.getDate()) ) {
            age--;
        }

        // Check if the user is old enough
        if( age < parseInt( minAge ) ) {
            return false;
        }

        return true;

    }

    const onValidateAge = () => {
        const isValid = isValidAge();

        // If the user is old enough, set the verification cookie
        if( isValid ) {
            Cookies.set( AgeVerificationCookieKey, 'true', { expires: 1 }); // Set cookie to expire in 1 day
            setIsVerified( true );
        } else {
            setIsVerified( false );
        }
    }

    const handleInputChange = ( field, e ) => {
        // Only allow numeric input
        if( !/^\d*$/.test(e.target.value) ) {
            e.target.value = e.target.value.replace(/\D/g, '');
        }

        const maxLength = field === 'year' ? 4 : 2;
        if( e.target.value.length > maxLength ) {
            e.target.value = e.target.value.slice(0, maxLength);
        }

        // For each field, check if it is more than allowed
        switch( field ) {
            case 'day':
                if( e.target.value > 31 ) {
                    e.target.value = '31';
                }
                break;

            case 'month':
                if( e.target.value > 12 ) {
                    e.target.value = '12';
                }
                break;
        }

        // Update the dateValues ref
        dateValues.current[field] = parseInt(e.target.value, 10) || null;

        // If the input is two digits, move to the next input
        if( e.target.value.length === maxLength ) {
            const nextInput = e.target.nextElementSibling;
            if( nextInput && nextInput.tagName === 'INPUT' ) {
                nextInput.focus();
            }
        }

        // Validate?
        if( field === 'year' && e.target.value.length === 4 ) {
            setButtonDisabled( !isAllFilledIn() );
        }
    }

    const onPressReset = () => {
        // Reset the date values
        dateValues.current = { day: null, month: null, year: null };
        setButtonDisabled( true );
        setIsVerified( null );
        Cookies.remove( AgeVerificationCookieKey ); // Remove the cookie
    }

    useEffect( () => {
        const handleKeyDown = (event) => {
			if (event.key === 'Enter') {
                if( isAllFilledIn() ) {
                    onValidateAge();
                }
			}
		};

		window.addEventListener('keydown', handleKeyDown);
		return () => {
			window.removeEventListener('keydown', handleKeyDown);
		};
    }, [] )

    if( loading ) {
        return <Loading />;
    }

    if( isVerified === true || minAge === null ) {
        return children;
    }

    if( isVerified === false ) {
        return (
            <div className="age-verification-container fixed-center">
                <div className="age-verification">
                    <h1>{ i18next.t('age_verification.error_message', { minAge }) }</h1>

                    <button className="small" onClick={ onPressReset }>{ i18next.t('age_verification.button_reset') }</button>
                </div>
            </div>
        );
    }

    return (
        <div className="age-verification-container fixed-center">
            <div className="age-verification">
                <h1>{ i18next.t('age_verification.title', { minAge }) }</h1>
                <h1>{ i18next.t('age_verification.message', { minAge }) }</h1>

                <div className="age-verification-inputs">
                    <div className="age-verification-input">
                        <div className="age-verification-input-inputs">
                            <input type="text" inputMode="numeric" pattern="[0-9]*" name="day" onChange={ handleInputChange.bind( this, 'day' ) } placeholder="DD"/>
                            <input type="text" inputMode="numeric" pattern="[0-9]*" name="month" onChange={ handleInputChange.bind( this, 'month' ) } placeholder="MM"/>
                            <input type="text" inputMode="numeric" pattern="[0-9]*" name="year" onChange={ handleInputChange.bind( this, 'year' ) } placeholder="YYYY"/>
                        </div>

                        <button 
                            className={ buttonDisabled ? "disabled" : "" }
                            onClick={ onValidateAge }
                            disabled={ buttonDisabled }>

                            { i18next.t('age_verification.button' ) }
                        </button>
                    </div>
                </div>

                {/* <div className="age-verification-buttons">
                    <button 
                        onClick={() => setIsVerified(true)}>
                            
                        { i18next.t('age_verification.button_accept', { minAge }) }
                    </button>

                    <button 
                        onClick={() => setIsVerified(false)}>
                            
                        { i18next.t('age_verification.button_decline', { minAge }) }
                    </button>
                </div> */}
            </div>
        </div>
    )
}

export default AgeVerification;